import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";

import { PaisViewModel } from "../models/centros/pais.model";
import { MasterService } from "../providers/master.service";

/**
 * Resuelve el código ISO2 de un país (para la bandera) a partir del propio ISO2,
 * del id de país o del nombre. Cachea el maestro de países (una sola llamada).
 */
@Injectable({ providedIn: "root" })
export class CountryService {
	private paises$?: Observable<PaisViewModel[]>;

	constructor(private readonly master: MasterService) {}

	private load(): Observable<PaisViewModel[]> {
		if (!this.paises$) {
			this.paises$ = this.master.getPaises().pipe(
				map((r) => r ?? []),
				catchError(() => of([] as PaisViewModel[])),
				shareReplay(1),
			);
		}
		return this.paises$;
	}

	/**
	 * Resuelve el ISO2 de un país para la bandera. Orden de preferencia:
	 *   1) ISO2 explícito (si la vertical ya lo trae).
	 *   2) PaisId interno → maestro (es la FK que devuelven casi todas las verticales; el más fiable).
	 *   3) Nombre (insensible a acentos/mayúsculas) como red de seguridad.
	 * Devuelve null si no hay coincidencia (o el maestro no tiene ISO2) → el componente mostrará solo el nombre.
	 */
	resolveIso2(opts: {
		iso2?: string | null;
		paisId?: number | null;
		name?: string | null;
	}): Observable<string | null> {
		if (opts.iso2 && opts.iso2.trim()) {
			return of(this._norm(opts.iso2));
		}
		if (opts.paisId == null && (!opts.name || !opts.name.trim())) {
			return of(null);
		}
		const target = opts.name ? this._key(opts.name) : null;
		return this.load().pipe(
			map((list) => {
				let p: PaisViewModel | undefined;
				if (opts.paisId != null) {
					p = list.find((x) => x.id === opts.paisId);
				}
				if (!p && target) {
					p = list.find((x) => this._key(x.descripcion) === target);
				}
				return p?.codigoIso2 ? this._norm(p.codigoIso2) : null;
			}),
		);
	}

	private _norm(s: string): string {
		return s.trim().toLowerCase();
	}

	// Clave de comparación insensible a acentos y mayúsculas.
	private _key(s?: string | null): string {
		return (s ?? "")
			.normalize("NFD")
			.replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
			.trim()
			.toLowerCase();
	}
}
